import { useCallback, useMemo } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { RootState, TypedDispatch } from 'redux/store'
import {
  setPaymentType,
  setPaymentMethod,
  setPaymentProcessor,
  setIsAmex as setIsAmexAction,
} from 'modules/order/state/payment-details/payment-details-slice'
import { PaymentMethod, PaymentProcessor } from 'models/ChargeIntent'
import { PaymentFinalizationData } from './payment-details-types'

const usePaymentDetails = () => {
  const dispatch = useDispatch<TypedDispatch<RootState>>()

  const { type, method, processor, isAmex } = useSelector(
    (state: RootState) => state.order.paymentDetails,
  )

  // Action to set the payment type (e.g., CC, PayPal)
  const setType = useCallback(
    (paymentType: PaymentMethod) => {
      dispatch(setPaymentType(paymentType))
    },
    [dispatch],
  )

  // Action to set the payment processor (e.g., Stripe, PayPal)
  const setProcessor = useCallback(
    (paymentProcessor: PaymentProcessor) => {
      dispatch(setPaymentProcessor(paymentProcessor))
    },
    [dispatch],
  )

  // Action to set the payment method details (e.g., after finalizing the payment)
  const setMethod = useCallback(
    (paymentMethodData: PaymentFinalizationData) => {
      dispatch(setPaymentMethod(paymentMethodData))
    },
    [dispatch],
  )

  // Action to set whether the payment method is Amex
  const setIsAmex = useCallback(
    (isAmex: boolean) => {
      dispatch(setIsAmexAction(isAmex))
    },
    [dispatch],
  )

  return useMemo(
    () => ({
      type,
      method,
      processor,
      setType,
      setProcessor,
      setMethod,
      setIsAmex,
      isAmex,
    }),
    [type, method, processor, setType, setProcessor, setMethod, setIsAmex, isAmex],
  )
}

export default usePaymentDetails
